import { Component, OnInit, OnDestroy, LOCALE_ID, ViewChild } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Router, ActivatedRoute, NavigationEnd } from '@angular/router';
import { Subject, interval } from 'rxjs';
import { takeUntil, debounceTime, distinctUntilChanged, filter } from 'rxjs/operators';
import { FormControl } from '@angular/forms';
import * as L from 'leaflet';
import { TranslateService, LangChangeEvent } from '@ngx-translate/core';
import { PageEvent } from '@angular/material/paginator';
import { Store } from '@ngrx/store';
import { CreateRequestDialogComponent } from '../create-request-dialog/create-request-dialog.component';
import { StatusType, DepartmentType, Request, RequestViewModel, RequestLocationAddress, RequestSubCategoryType } from '../../models/request.model';
import { ExportFormat } from '../export-menu/export-menu.component';
import { FilterOptions, FilterMenuItem, SavedFilter } from '../filter-menu/filter-menu.component';
import { RequestFilterSidenavComponent } from '../request-filter-sidenav/request-filter-sidenav.component';
import { RequestsV2ImplService } from '../../services/requests-v2.impl.service';
import { RequestStatusService } from '../../services/request-status.service';
import { State } from '../../../store';
import { organisationSelector } from '../../../global/store/global.selectors';
import { Organisation } from '../../../global/global.model';
import { GetProfileAction, SetProfileAction, UpdateProfileAction } from 'src/app/user/store/user.actions';
import { Actions, ofType } from '@ngrx/effects';
import { MAP_TILE_ATTRIBUTION, MAP_TILE_URL } from 'src/app/global/consts/map-tiles.const';

@Component({
    selector: 'app-request-list-page',
    templateUrl: './request-list-page.component.html',
    styleUrls: ['./request-list-page.component.scss'],
    providers: [{ provide: LOCALE_ID, useValue: 'fr-CA' }],
})
export class RequestListPageComponent implements OnInit, OnDestroy {
    private destroy$ = new Subject<void>();

    @ViewChild(RequestFilterSidenavComponent) filterSidenav: RequestFilterSidenavComponent;

    private refreshInterval$ = new Subject<void>();

    searchControl = new FormControl('');

    private readonly REFRESH_INTERVAL_MS = 2.5 * 60 * 1000;

    private map: L.Map;

    private markers: L.Marker[] = [];

    private defaultLocation: [number, number] = [45.5017, -73.5673];

    private defaultZoom = 13;

    private zoomThreshold = 14;

    selectedCategory: any = null;

    requests: RequestViewModel[] = [];

    filteredRequests: RequestViewModel[] = [];

    activeView: 'list' | 'map' = 'list';

    loading = false;

    showMarkerText = true;

    columnHeaders = [
        {
            field: 'subject',
            translationKey: 'request_v2.list.subject',
            sortable: true,
            sortField: 'subCategory',
        },
        {
            field: 'category',
            translationKey: 'request_v2.list.category',
            sortable: true,
            sortField: 'category',
        },
        {
            field: 'requestId',
            translationKey: 'request_v2.list.request',
            sortable: true,
            sortField: 'requestId',
        },
        {
            field: 'location',
            translationKey: 'request_v2.list.location',
            sortable: false,
        },
        {
            field: 'creationDate',
            translationKey: 'request_v2.list.creation_date',
            sortable: true,
            sortField: 'createdAt',
        },
        {
            field: 'status',
            translationKey: 'request_v2.list.status',
            sortable: true,
            sortField: 'status',
        },
        {
            field: 'department',
            translationKey: 'request_v2.list.department',
            sortable: true,
            sortField: 'department',
        },
        {
            field: 'tags',
            translationKey: 'request_v2.list.tags',
            sortable: false,
        },
        {
            field: 'reporter',
            translationKey: 'request_v2.list.reporter',
            sortable: false,
        },
        {
            field: 'sla',
            translationKey: 'request_v2.list.sla_date',
            sortable: false,
            sortField: 'resolvedAt',
        },
    ];

    // Pagination properties
    length = 0;

    pageSize = 10;

    pageIndex = 0;

    pageSizeOptions = [10, 25, 50];

    filters: FilterOptions = {
        department: null,
        category: null,
        subCategory: null,
        user: null,
        status: null,
        createdAtFrom: null,
        createdAtTo: null,
        slaDate: null,
        requestType: null,
        source: null,
        recurring: null,
        search: '',
        sortBy: 'createdAt',
        sortOrder: 'desc',
        tags: null,
        tagsFilterMode: 'OR',
    };

    departmentTypes: DepartmentType[] = [];

    statusTypes: StatusType[] = [];

    categories: FilterMenuItem[] = [];

    subcategories: FilterMenuItem[] = [];

    users: FilterMenuItem[] = [];

    sourcesTypes: FilterMenuItem[] = [];

    currentLocale: string;

    currentOrg = null;

    savedFilters: SavedFilter[] = [];
    private currentProfileFilters: any[] = [];
    private skipNextProfileReload = false;

    constructor(
        private dialog: MatDialog,
        private router: Router,
        private route: ActivatedRoute,
        private requestsService: RequestsV2ImplService,
        private requestStatusService: RequestStatusService,
        public translate: TranslateService,
        private store: Store<State>,
        private actions$: Actions,
    ) {}

    ngOnInit(): void {
        this.store
            .select(organisationSelector)
            .pipe(takeUntil(this.destroy$))
            .subscribe((organisation: Organisation) => {
                this.currentOrg = organisation.tag;
                if (organisation?.bounds?.center && (organisation.bounds.center.lon !== this.defaultLocation[0] || organisation.bounds.center.lat !== this.defaultLocation[1])) {
                    this.defaultLocation = [organisation.bounds.center.lat, organisation.bounds.center.lon];
                }
            });

        this.store.dispatch(GetProfileAction());

        this.actions$.pipe(ofType(SetProfileAction), takeUntil(this.destroy$)).subscribe(({ profile }) => {
            const allFilters = profile?.preferences?.requestFilters ?? [];
            this.currentProfileFilters = allFilters;
            const orgEntry = allFilters.find((item) => item.organisation === this.currentOrg);

            if (orgEntry) {
                // Backward-compatible read: support both flat and nested shapes
                const lastUsed = orgEntry.lastUsed ?? orgEntry;
                const toFilterString = (val: string | string[] | null | undefined): string | null => {
                    if (val === null || val === undefined) return null;
                    if (Array.isArray(val)) return val.join(',') || null;
                    return val;
                };

                // Load saved filters (always update from profile)
                this.savedFilters = orgEntry.savedFilters ?? [];

                // Skip full reload when this was triggered by our own save/delete
                if (this.skipNextProfileReload) {
                    this.skipNextProfileReload = false;
                    return;
                }

                this.filters = {
                    ...this.filters,
                    ...lastUsed,
                    department: toFilterString(lastUsed.department),
                    category: toFilterString(lastUsed.category),
                    subCategory: toFilterString(lastUsed.subCategory),
                    status: toFilterString(lastUsed.status),
                    source: toFilterString(lastUsed.source),
                    requester: toFilterString(lastUsed.requester),
                    assignee: toFilterString(lastUsed.assignee),
                };
                this.searchControl.setValue(this.filters.search || '', { emitEvent: false });
            }

            this.loadFilterOptions();
            this.loadRequests();
        });

        this.setupAutoRefresh();
        this.setupSearchListener();
        this.router.events
            .pipe(
                filter((e) => e instanceof NavigationEnd),
                filter((e: NavigationEnd) => e.urlAfterRedirects === '/requests-v2/manage'),
                takeUntil(this.destroy$),
            )
            .subscribe(() => {
                if ((history.state as { requestModified?: boolean })?.requestModified) {
                    this.loadRequests();
                }
            });
        this.setLocale(this.translate.currentLang);
        this.translate.onLangChange.pipe(takeUntil(this.destroy$)).subscribe((event: LangChangeEvent) => {
            this.setLocale(event.lang);
            this.refreshRequestsData();
        });
    }

    private setLocale(lang: string): void {
        this.currentLocale = lang === 'fr' ? 'fr-CA' : 'en-CA';
    }

    private setupSearchListener(): void {
        this.searchControl.valueChanges.pipe(debounceTime(500), distinctUntilChanged(), takeUntil(this.destroy$)).subscribe((value) => {
            this.filters.search = value;
            this.pageIndex = 0;
            this.loadRequests();
        });
    }

    clearSearch(): void {
        this.searchControl.setValue('');
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
        this.refreshInterval$.next();
        this.refreshInterval$.complete();
        if (this.map) {
            this.map.remove();
        }
    }

    private setupAutoRefresh(): void {
        interval(this.REFRESH_INTERVAL_MS)
            .pipe(takeUntil(this.refreshInterval$), takeUntil(this.destroy$))
            .subscribe(() => {
                this.refreshRequestsData();
            });
    }

    private refreshRequestsData(): void {
        const currentPage = this.pageIndex + 1;

        this.refreshFilterOptions();

        this.requestsService
            .getRequests(this.filters, currentPage, this.pageSize)
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: (response) => {
                    const updatedRequests = this.mapRequestsToViewModel(response.docs);
                    this.requests = updatedRequests;
                    this.length = response.totalDocs || response.docs.length;

                    this.applyFilters();

                    if (this.activeView === 'map') {
                        this.updateMapMarkers();
                    }
                },
                error: (error) => {
                    console.error('Error refreshing requests data:', error);
                },
            });
    }

    private refreshFilterOptions(): void {
        this.requestsService.getDepartments().subscribe(
            (response) => {
                if (response && response.length > 0) {
                    this.departmentTypes = response;
                } else {
                    this.departmentTypes = [];
                }
            },
            (_error) => {
                this.departmentTypes = [];
            },
        );

        this.requestsService.getRequestCategories().subscribe(
            (categories) => {
                if (categories && categories.length > 0) {
                    this.categories = categories;

                    const subcatMap = new Map();
                    categories
                        .filter((cat) => (cat as any).subCategories && (cat as any).subCategories.length > 0)
                        .forEach((cat) => {
                            const rawCat = cat as any;
                            rawCat.subCategories.forEach((subCat: any) => {
                                if (!subcatMap.has(subCat._id)) {
                                    subcatMap.set(subCat._id, {
                                        id: subCat._id,
                                        name: subCat.name,
                                        categoryId: cat.id || cat._id,
                                    });
                                }
                            });
                        });
                    this.subcategories = Array.from(subcatMap.values());
                } else {
                    this.categories = [];
                    this.subcategories = [];
                }
            },
            (_error) => {
                this.categories = [];
                this.subcategories = [];
            },
        );

        this.requestStatusService.getAllAvailableToOrg().subscribe(
            (statuses) => {
                if (statuses && statuses.length > 0) {
                    this.statusTypes = statuses as any;
                } else {
                    this.statusTypes = [];
                }
            },
            (_error) => {
                this.statusTypes = [];
            },
        );
    }

    loadRequests(): void {
        this.loading = true;
        const currentPage = this.pageIndex + 1;
        this.requestsService
            .getRequests(this.filters, currentPage, this.pageSize)
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: (response) => {
                    this.requests = this.mapRequestsToViewModel(response.docs);
                    this.length = response.totalDocs || response.docs.length;
                    this.applyFilters();
                    this.loading = false;
                    if (this.activeView === 'map') {
                        this.updateMapMarkers();
                    }
                },
                error: (_error) => {
                    this.loading = false;
                    this.requests = [];
                    this.filteredRequests = [];
                    this.length = 0;
                },
            });
    }

    private mapRequestsToViewModel(requests: Request[]): RequestViewModel[] {
        return requests.map((request) => this.mapRequestToViewModel(request));
    }

    private mapRequestToViewModel(request: Request): RequestViewModel {
        const coordinates = request.location?.geometry?.coordinates;
        let validCoordinates: [number, number] | undefined;

        if (Array.isArray(coordinates) && coordinates.length >= 2 && !isNaN(Number(coordinates[0])) && !isNaN(Number(coordinates[1]))) {
            validCoordinates = [Number(coordinates[0]), Number(coordinates[1])];
        }

        const expectedEnd = request.sla ?? undefined;

        return {
            _id: request._id,
            category: {
                _id: request.category._id,
                name: request.category.name, // Main category name
                color: request.category.color,
                icon: request.category.icon,
            },
            requestId: request.requestId,
            subCategory: request.subCategory,
            location: this.formatAddress(request.location?.address),
            coordinates: validCoordinates,
            creationDate: request.createdAt ? request.createdAt.toString() : '',
            status: request.category.statuses?.find((status) => status._id === request.state),
            categoryStatuses: request.category.statuses || [],
            department: request.department || null,
            reporter: request.user
                ? {
                      _id: request.user._id,
                      firstName: request.user.firstName,
                      lastName: request.user.lastName,
                      email: request.user.email,
                      picture: request.user.picture,
                  }
                : null,
            reporterType: 'user',
            createdAt: request.createdAt ? request.createdAt.toString() : '',
            expectedEnd,
            isPrivate: request.isPrivate || false,
            adminUnreadMessageCount: request.adminUnreadMessageCount,
            tags: request.tags || [],
        };
    }

    private formatAddress(address?: RequestLocationAddress): string {
        if (!address) return '';

        const parts = [];

        if (address.number) parts.push(address.number);
        if (address.streetName) parts.push(address.streetName);
        if (!address.streetName && address.street) parts.push(address.street);
        if (address.city) parts.push(address.city);
        if (address.province) parts.push(address.province);
        if (address.country) parts.push(address.country);

        return parts.join(', ');
    }

    toggleView(view: 'list' | 'map'): void {
        this.activeView = view;
        if (view === 'map') {
            setTimeout(() => {
                this.initializeMap();
                if (this.filteredRequests.length > 0) {
                    this.updateMapMarkers();
                }
            }, 100);
        }
    }

    private initializeMap(): void {
        if (this.map) {
            this.map.remove();
        }

        this.map = L.map('request-map', {
            zoomControl: false,
        }).setView(this.defaultLocation, this.defaultZoom);

        L.control
            .zoom({
                position: 'topright',
            })
            .addTo(this.map);

        L.tileLayer(MAP_TILE_URL, {
            maxZoom: 18,
            minZoom: 13,
            attribution: MAP_TILE_ATTRIBUTION,
        }).addTo(this.map);

        this.showMarkerText = this.map.getZoom() >= this.zoomThreshold;

        this.map.on('zoomend', () => {
            const newShowMarkerText = this.map.getZoom() >= this.zoomThreshold;
            if (newShowMarkerText !== this.showMarkerText) {
                this.showMarkerText = newShowMarkerText;
                this.updateMarkerIcons();
            }
        });

        this.updateMapMarkers();
    }

    private updateMarkerIcons(): void {
        if (!this.map) return;

        this.filteredRequests.forEach((request, index) => {
            if (index < this.markers.length && request.coordinates) {
                const markerIcon = this.createCustomMarkerIcon(
                    request.category.icon || 'fas fa-map-marker-alt',
                    request.category.name || 'Request',
                    request.requestId || '',
                    request.category.color || '#333333',
                );

                this.markers[index].setIcon(markerIcon);
            }
        });
    }

    getInitials(name: string): string {
        if (!name) return '';

        const parts = name.trim().split(' ');
        if (parts.length === 1) {
            return parts[0].charAt(0).toUpperCase();
        }

        return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
    }

    private createCustomMarkerIcon(iconClass: string, categoryText: string, idText: string, color: string): L.DivIcon {
        const colors = {
            fill: this.getMarkerBackgroundColor(color),
            stroke: color,
        };

        const markerHtml = `
            <div class="pin-container">
                <div class="marker-content">
                    <div class="svg-container" >
                        <svg  viewBox="0 0 26 33" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <g id="Vector" filter="url(#filter0_d_306_3188)">
                                <path d="M12.9543 1C17.505 1 20.9086 5.01135 20.9086 10.1343C20.9086 14.7536 16.8923 20.6793 13.617 23.436C13.2261 23.7638 12.6825 23.7638 12.2916 23.436C9.01714 20.6813 5 14.7536 5 10.1343C5 5.01135 8.40352 1 12.9543 1Z" fill="${colors.fill}"/>
                                <path d="M13.9383 23.8191L13.9389 23.8185C15.626 22.3986 17.484 20.1822 18.922 17.7371C20.3555 15.2996 21.4086 12.5723 21.4086 10.1343C21.4086 4.80632 17.8473 0.5 12.9543 0.5C8.06127 0.5 4.5 4.80632 4.5 10.1343C4.5 12.5723 5.55328 15.3002 6.98688 17.7378C8.42498 20.1832 10.283 22.3996 11.9697 23.8186L11.9703 23.8191C12.547 24.3027 13.3615 24.3027 13.9383 23.8191Z" stroke="${colors.stroke}"/>
                            </g>
                            <defs>
                                <filter id="filter0_d_306_3188" x="0" y="0" width="25.9086" height="32.6819" filterUnits="userSpaceOnUse" color-interpolation-filters="sRGB">
                                    <feFlood flood-opacity="0" result="BackgroundImageFix"/>
                                    <feColorMatrix in="SourceAlpha" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0" result="hardAlpha"/>
                                    <feOffset dy="4"/>
                                    <feGaussianBlur stdDeviation="2"/>
                                    <feComposite in2="hardAlpha" operator="out"/>
                                    <feColorMatrix type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.25 0"/>
                                    <feBlend mode="normal" in2="BackgroundImageFix" result="effect1_dropShadow_306_3188"/>
                                    <feBlend mode="normal" in="SourceGraphic" in2="effect1_dropShadow_306_3188" result="shape"/>
                                </filter>
                            </defs>
                        </svg>
                        <i class="${iconClass} marker-icon"  style="color: ${colors.stroke};"></i>

                    </div>
                    ${
                        this.showMarkerText
                            ? `
                    <div class="pin-text">
                        <div class="category-text" style="color: #000000; font-size: 14px;">${categoryText}</div>
                        <div class="id-text" style="color: #000000; font-size: 13px;">${idText}</div>
                    </div>
                    `
                            : ''
                    }
                </div>
            </div>
        `;

        return L.divIcon({
            html: markerHtml,
            className: 'custom-div-icon',
            iconSize: [250, 60],
            iconAnchor: [20, 50],
        });
    }

    private updateMapMarkers(): void {
        if (!this.map) return;

        const currentCenter = this.map.getCenter();
        const currentZoom = this.map.getZoom();

        this.markers.forEach((marker) => marker.remove());
        this.markers = [];

        this.filteredRequests.forEach((request) => {
            if (request.coordinates && request.coordinates.length === 2 && !isNaN(request.coordinates[0]) && !isNaN(request.coordinates[1])) {
                const latLng: L.LatLngExpression = [request.coordinates[1], request.coordinates[0]];

                const markerIcon = this.createCustomMarkerIcon(
                    request.category.icon || 'fas fa-map-marker-alt',
                    request.category.name || 'Request',
                    request.requestId || '',
                    request.category.color || '#333333',
                );

                const marker = L.marker(latLng, {
                    icon: markerIcon,
                }).addTo(this.map);

                marker.on('click', () => {
                    this.openRequestDetail(request);
                });

                this.markers.push(marker);
            }
        });

        if (this.markers.length > 0 && !currentCenter) {
            const group = L.featureGroup(this.markers);
            this.map.fitBounds(group.getBounds().pad(0.1));
        } else if (currentCenter) {
            this.map.setView(currentCenter, currentZoom);
        } else {
            this.map.setView(this.defaultLocation, this.defaultZoom);
        }
    }

    onFilterChanged(filterOptions: FilterOptions): void {
        this.filters = {
            ...this.filters,
            ...filterOptions,
            search: this.filters.search,
            sortBy: this.filters.sortBy,
            sortOrder: this.filters.sortOrder,
        };
        this.pageIndex = 0;
        this.loadRequests();
    }

    hasActiveFilters(): boolean {
        const hasSearch = !!this.filters.search;
        const hasMenuFilters =
            this.filters.department !== null ||
            this.filters.category !== null ||
            this.filters.subCategory !== null ||
            this.filters.user !== null ||
            this.filters.status !== null ||
            this.filters.createdAtFrom !== null ||
            this.filters.createdAtTo !== null ||
            this.filters.slaDate !== null ||
            this.filters.requestType !== null ||
            this.filters.source !== null ||
            this.filters.recurring !== null;

        const hasSortFilters = this.filters.sortBy !== 'createdAt' || this.filters.sortOrder !== 'desc';

        return hasSearch || hasMenuFilters || hasSortFilters;
    }

    activeFilterCount(): number {
        const hasValue = (val: any) => val !== null && val !== undefined && val !== '' && !(Array.isArray(val) && val.length === 0);
        let count = 0;
        if (hasValue(this.filters.search)) count++;
        if (hasValue(this.filters.department)) count++;
        if (hasValue(this.filters.category)) count++;
        if (hasValue(this.filters.subCategory)) count++;
        if (hasValue(this.filters.status)) count++;
        if (this.filters.createdAtFrom !== null || this.filters.createdAtTo !== null) count++;
        if (hasValue(this.filters.requester)) count++;
        if (hasValue(this.filters.assignee)) count++;
        return count;
    }

    clearAllFilters(): void {
        this.clearSearch();
        if (this.filterSidenav) {
            this.filterSidenav.reset();
        } else {
            this.filters = {
                ...this.filters,
                department: null,
                category: null,
                subCategory: null,
                user: null,
                status: null,
                createdAtFrom: null,
                createdAtTo: null,
                slaDate: null,
                requestType: null,
                source: null,
                recurring: null,
            };
        }

        // Reset sort to default
        this.filters.sortBy = 'createdAt';
        this.filters.sortOrder = 'desc';

        this.loadRequests();
    }

    onSaveFilter(event: { name: string; filters: FilterOptions }): void {
        const newFilter: SavedFilter = {
            id: Math.random().toString(36).slice(2) + Date.now().toString(36),
            name: event.name,
            filters: event.filters,
        };
        this.savedFilters = [...this.savedFilters, newFilter];
        this.persistFiltersToProfile();
    }

    onDeleteFilter(filterId: string): void {
        this.savedFilters = this.savedFilters.filter((sf) => sf.id !== filterId);
        this.persistFiltersToProfile();
    }

    onLoadFilter(savedFilter: SavedFilter): void {
        this.onFilterChanged(savedFilter.filters);
        if (this.filterSidenav) {
            this.filterSidenav.patchFormWithFilters(savedFilter.filters);
        }
    }

    private persistFiltersToProfile(): void {
        const allFilters = [...this.currentProfileFilters];
        const orgIndex = allFilters.findIndex((item) => item.organisation === this.currentOrg);

        const orgEntry = {
            organisation: this.currentOrg,
            lastUsed: {
                department: this.filters.department,
                category: this.filters.category,
                subCategory: this.filters.subCategory,
                status: this.filters.status,
                source: this.filters.source,
                requester: this.filters.requester,
                assignee: this.filters.assignee,
                createdAtFrom: this.filters.createdAtFrom,
                createdAtTo: this.filters.createdAtTo,
                slaDate: this.filters.slaDate,
                search: this.filters.search,
                sortBy: this.filters.sortBy,
                sortOrder: this.filters.sortOrder,
            },
            savedFilters: this.savedFilters,
        };

        if (orgIndex >= 0) {
            allFilters[orgIndex] = orgEntry;
        } else {
            allFilters.push(orgEntry);
        }

        this.currentProfileFilters = allFilters;
        this.skipNextProfileReload = true;
        this.store.dispatch(
            UpdateProfileAction({
                payload: { preferences: { requestFilters: allFilters } },
            }),
        );
    }

    openCreateRequestDialog(): void {
        const dialogRef = this.dialog.open(CreateRequestDialogComponent, {
            width: '80vw',
            height: '90vh',
            panelClass: 'fullscreen-dialog',
        });

        dialogRef.afterClosed().subscribe((result) => {
            if (result) {
                this.loadRequests();
            }
        });
    }

    openRequestDetail(request: RequestViewModel): void {
        this.router.navigate(['details', request._id], { relativeTo: this.route });
    }

    openDepartmentSelect(request: RequestViewModel): void {
        const departmentSelects = document.querySelectorAll('app-department-select');
        let found = false;

        departmentSelects.forEach((select) => {
            const selectEl = select as any;
            if (selectEl.requestId === request._id) {
                const clickEvent = new MouseEvent('click', {
                    bubbles: true,
                    cancelable: true,
                    view: window,
                });
                selectEl.dispatchEvent(clickEvent);
                found = true;
            }
        });

        if (!found) {
            this.onDepartmentChange({
                requestId: request._id,
                departmentId: null,
                department: null,
            });
        }
    }

    private loadFilterOptions(): void {
        this.loading = true;

        this.requestsService.getDepartments().subscribe(
            (response) => {
                if (response && response.length > 0) {
                    this.departmentTypes = response;
                } else {
                    this.departmentTypes = [];
                }
            },
            (_error) => {
                this.departmentTypes = [];
            },
        );

        this.requestsService.getRequestCategories().subscribe(
            (categories) => {
                if (categories && categories.length > 0) {
                    this.categories = categories;

                    const subcatMap = new Map();
                    categories
                        .filter((cat) => (cat as any).subCategories && (cat as any).subCategories.length > 0)
                        .forEach((cat) => {
                            const rawCat = cat as any;
                            rawCat.subCategories.forEach((subCat: any) => {
                                if (!subcatMap.has(subCat._id)) {
                                    subcatMap.set(subCat._id, {
                                        id: subCat._id,
                                        name: subCat.name,
                                        categoryId: cat.id || cat._id,
                                    });
                                }
                            });
                        });
                    this.subcategories = Array.from(subcatMap.values());
                } else {
                    this.categories = [];
                    this.subcategories = [];
                }
                this.loading = false;
            },
            (_error) => {
                this.categories = [];
                this.subcategories = [];
                this.loading = false;
            },
        );

        this.requestStatusService.getAllAvailableToOrg().subscribe(
            (statuses) => {
                if (statuses && statuses.length > 0) {
                    this.statusTypes = statuses as any;
                } else {
                    this.statusTypes = [];
                }
            },
            (_error) => {
                this.statusTypes = [];
            },
        );

        this.users = [];
        this.sourcesTypes = [];
    }

    applyFilters(): void {
        this.filteredRequests = [...this.requests];

        if (this.activeView === 'map') {
            this.updateMapMarkers();
        }
    }

    exportAs(_format: ExportFormat): void {
        // TODO: Implement export functionality
    }

    onStatusChange(event: { requestId: string; statusId: string; status: StatusType }): void {
        const requestIndex = this.requests.findIndex((r) => r._id === event.requestId);
        if (requestIndex !== -1) {
            this.requests[requestIndex].status = event.status;

            const filteredIndex = this.filteredRequests.findIndex((r) => r._id === event.requestId);
            if (filteredIndex !== -1) {
                this.filteredRequests[filteredIndex].status = event.status;
            }
        }
    }

    onDepartmentChange(event: { requestId: string; departmentId: string | null; department: DepartmentType | null }): void {
        const requestIndex = this.requests.findIndex((r) => r._id === event.requestId);
        if (requestIndex !== -1) {
            this.requests[requestIndex].department = event.department;

            const filteredIndex = this.filteredRequests.findIndex((r) => r._id === event.requestId);
            if (filteredIndex !== -1) {
                this.filteredRequests[filteredIndex].department = event.department;
            }
        }
    }

    formatDate(date: string): string {
        if (!date) {
            return '';
        }

        const dateObj = new Date(date);
        return dateObj.toLocaleDateString(`${this.translate.currentLang}`, {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
        });
    }

    getMarkerBackgroundColor(color: string): string {
        if (!color) return '#f5f5f5';

        const hex = color.replace('#', '');
        if (hex.length !== 6) return '#f5f5f5'; // Basic validation

        let r = parseInt(hex.substring(0, 2), 16);
        let g = parseInt(hex.substring(2, 4), 16);
        let b = parseInt(hex.substring(4, 6), 16);

        const lightenFactor = 0.8;
        r = Math.round(r + (255 - r) * lightenFactor);
        g = Math.round(g + (255 - g) * lightenFactor);
        b = Math.round(b + (255 - b) * lightenFactor);

        // Ensure values are within 0-255 range
        r = Math.max(0, Math.min(255, r));
        g = Math.max(0, Math.min(255, g));
        b = Math.max(0, Math.min(255, b));

        // Convert back to hex, ensuring 2 digits per component
        const rHex = r.toString(16).padStart(2, '0');
        const gHex = g.toString(16).padStart(2, '0');
        const bHex = b.toString(16).padStart(2, '0');

        return `#${rHex}${gHex}${bHex}`;
    }

    getChipBackgroundColor(color: string, opacity = 0.15): string {
        if (!color) return 'rgba(0, 0, 0, 0.15)';

        const hex = color.replace('#', '');
        const r = parseInt(hex.substring(0, 2), 16);
        const g = parseInt(hex.substring(2, 4), 16);
        const b = parseInt(hex.substring(4, 6), 16);

        return `rgba(${r}, ${g}, ${b}, ${opacity})`;
    }

    // Handle page change event from paginator
    pageChange(event: PageEvent): void {
        this.pageSize = event.pageSize;
        this.pageIndex = event.pageIndex;
        this.loadRequests();
    }

    getSubcategoriesForCategory(categoryId: string): RequestSubCategoryType[] {
        // Find the category in the loaded categories list
        const rawCategories = this.categories as any[];
        const category = rawCategories.find((c) => c.id === categoryId || c._id === categoryId);

        if (category && category.subCategories) {
            return category.subCategories;
        }
        return [];
    }

    sort(header: any): void {
        if (!header.sortable) {
            return;
        }

        if (this.filters.sortBy === header.sortField) {
            this.filters.sortOrder = this.filters.sortOrder === 'asc' ? 'desc' : 'asc';
        } else {
            this.filters.sortBy = header.sortField;
            this.filters.sortOrder = 'asc';
        }

        this.pageIndex = 0;
        this.loadRequests();
    }
}
